import React, { useCallback, useMemo } from 'react';
import Color from 'color';
import { ExportOutlined } from '@ant-design/icons';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { changeFilterValueToArrayOfIds, nullSafeGet } from '../../utils/DataAccessUtils';
import {
	COMPARISON_MODE,
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
	formatComparisonRangeLabel,
	getAnalyticsRolePrimaryColor,
	resolveComparisonRange,
} from '../../utils/AnalyticsComparisonUtils';
import { buildGroupedComparisonTooltipHtml } from '../../utils/AnalyticsComparisonTooltipUtils';
import {
	PERFORMER_SEGMENT,
	segmentSupplierPerformers,
} from '../../utils/SupplierPerformerSegmentationUtils';
import {
	formatDurationShort,
	roundToOneDecimalPlace,
} from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsUtils';
import { PERFORMER_SHAPE } from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsConstants';
import {
	formatDurationCell,
	formatFirstTimeFixRate,
	getDefaultSupplierPerformanceFilters,
	normalizeSupplierPerformanceRows,
} from './SupplierPerformanceUtils';
import { buildSupplierPerformanceTableColumns } from './SupplierPerformanceTableColumns';
import {
	DEFAULT_MIN_WO_COUNT,
	MAX_SUPPLIER_LABEL_CHARS,
	MAX_SUPPLIERS_IN_CHART,
	PERFORMER_GROUP_LABELS,
	PERFORMER_GROUP_LABEL_ORDER,
	COMPARISON_CURRENT_POINT_SIZE,
	COMPARISON_POINT_BORDER_WIDTH,
	COMPARISON_PREVIOUS_BORDER_COLOR,
	COMPARISON_PREVIOUS_POINT_SIZE,
	buildComparisonConnectorEdgeStyle,
	isValidComparisonConnectorEdge,
} from './SupplierPerformanceConstants';

const STAR_SIZE_BOOST = 1.2;
const HOVER_SIZE_BOOST = 1.2;
const HOVER_CONNECTOR_LINE_WIDTH = 4;
const HOVER_POINT_DARKEN_AMOUNT = 0.2;
const UNHOVERED_POINT_LIGHTEN_AMOUNT = 0.35;

const buildRowsById = (rows: any[]): Map<any, any> => {
	const rowsById = new Map();
	rows.forEach((row) => rowsById.set(row.supplierFacilityId, row));
	return rowsById;
};

const buildTooltipMetricRows = (responseSeconds: any, resolutionSeconds: any) => [
	{ label: 'Response time', value: formatDurationShort(responseSeconds) },
	{ label: 'Resolution time', value: formatDurationShort(resolutionSeconds) },
];

export function useSupplierPerformancePresentation(data: any, hoveredSupplierId?: any) {
	const {
		userType,
		companyConfig,
		loading,
		error,
		supplierPerformance,
		previousSupplierPerformance,
		comparisonState,
		comparisonLoading,
		hasFullCompletedAtRange,
		activeFilters,
		minWoCount,
		setMinWoCount,
		showSupplierLabels,
		setShowSupplierLabels,
		suppliersPanelOpen,
		setSuppliersPanelOpen,
		handleComparisonChange,
		updateFilters,
		clearAllFilters,
		navigateToSupplierDetail,
		navigateToFindVendors,
	} = data;

	const chartColor =
		nullSafeGet('detail.config.theme.navblock', companyConfig) ||
		getAnalyticsRolePrimaryColor(userType);

	const darkChartColor = useMemo(() => {
		try {
			return Color(chartColor).darken(HOVER_POINT_DARKEN_AMOUNT).hex();
		} catch (e) {
			return chartColor;
		}
	}, [chartColor]);

	const lightChartColor = useMemo(() => {
		try {
			return Color(chartColor).lighten(UNHOVERED_POINT_LIGHTEN_AMOUNT).hex();
		} catch (e) {
			return chartColor;
		}
	}, [chartColor]);

	const isHoverActive = hoveredSupplierId !== null && hoveredSupplierId !== undefined;

	const resolveCurrentSeriesPointColor = useCallback(
		(supplierFacilityId: any) => {
			if (!isHoverActive) {
				return chartColor;
			}
			return supplierFacilityId === hoveredSupplierId ? darkChartColor : lightChartColor;
		},
		[isHoverActive, hoveredSupplierId, chartColor, darkChartColor, lightChartColor]
	);

	const supplierFacilityIdsFilter = activeFilters.supplierFacilityIds;

	const applySupplierFilter = useCallback(
		(rows: any[]) => {
			const allowedIds = changeFilterValueToArrayOfIds(supplierFacilityIdsFilter);
			if (allowedIds.length === 0) return rows;
			const allowedIdSet = new Set(allowedIds);
			return rows.filter((row) => allowedIdSet.has(row.supplierFacilityId));
		},
		[supplierFacilityIdsFilter]
	);

	const normalizedRows = useMemo(
		() =>
			applySupplierFilter(normalizeSupplierPerformanceRows(supplierPerformance)).sort(
				(a, b) => (b.woCount || 0) - (a.woCount || 0)
			),
		[supplierPerformance, applySupplierFilter]
	);

	const panelRows = useMemo(
		() => normalizedRows.filter((row) => minWoCount <= 0 || row.woCount >= minWoCount),
		[normalizedRows, minWoCount]
	);

	const currentChartRows = useMemo(() => {
		const eligible = panelRows.filter((row) => row.hasChartMetrics);
		const segments = segmentSupplierPerformers(
			eligible.map((row) => ({
				id: row.supplierFacilityId,
				responseValue: row.avgResponseTimeSeconds,
				resolutionValue: row.avgResolutionTimeSeconds,
			}))
		);
		return eligible.map((row) => ({
			...row,
			performerGroup:
				PERFORMER_GROUP_LABELS[segments.get(row.supplierFacilityId) || PERFORMER_SEGMENT.TYPICAL],
		}));
	}, [panelRows]);

	const previousChartRows = useMemo(() => {
		const normalized = applySupplierFilter(
			normalizeSupplierPerformanceRows(previousSupplierPerformance)
		);
		const eligible = normalized.filter(
			(row) => row.hasChartMetrics && (minWoCount <= 0 || row.woCount >= minWoCount)
		);
		const segments = segmentSupplierPerformers(
			eligible.map((row) => ({
				id: row.supplierFacilityId,
				responseValue: row.avgResponseTimeSeconds,
				resolutionValue: row.avgResolutionTimeSeconds,
			}))
		);
		return eligible.map((row) => ({
			...row,
			performerGroup:
				PERFORMER_GROUP_LABELS[segments.get(row.supplierFacilityId) || PERFORMER_SEGMENT.TYPICAL],
		}));
	}, [previousSupplierPerformance, applySupplierFilter, minWoCount]);

	const comparisonMode = comparisonState ? comparisonState.mode : COMPARISON_MODE.NONE;
	const hasComparison = comparisonMode !== COMPARISON_MODE.NONE && previousChartRows.length > 0;

	const selectedRange = hasFullCompletedAtRange
		? [activeFilters.startDate, activeFilters.endDate]
		: null;
	const comparisonRange = selectedRange
		? resolveComparisonRange(selectedRange[0], selectedRange[1], comparisonState)
		: null;

	const currentSeriesData = useMemo(
		() =>
			currentChartRows
				.slice(0, MAX_SUPPLIERS_IN_CHART)
				.map((row) => ({ ...row, [COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_CURRENT })),
		[currentChartRows]
	);

	const previousSeriesData = useMemo(() => {
		if (!hasComparison) return [];
		return previousChartRows.slice(0, MAX_SUPPLIERS_IN_CHART).map((row) => ({
			...row,
			[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_PREVIOUS,
		}));
	}, [hasComparison, previousChartRows]);

	const chartData = useMemo(
		() => (hasComparison ? [...previousSeriesData, ...currentSeriesData] : currentSeriesData),
		[hasComparison, currentSeriesData, previousSeriesData]
	);

	const edges = useMemo(() => {
		if (!hasComparison) return undefined;
		const currentById = buildRowsById(currentSeriesData);
		const edgeRows = [];
		previousSeriesData.forEach((previousRow) => {
			const currentRow = currentById.get(previousRow.supplierFacilityId);
			if (!currentRow) return;
			const previousX = previousRow.avgResponseTimeMinutes;
			const previousY = previousRow.avgResolutionMinutes;
			const currentX = currentRow.avgResponseTimeMinutes;
			const currentY = currentRow.avgResolutionMinutes;
			if (!isValidComparisonConnectorEdge(previousX, previousY, currentX, currentY)) {
				return;
			}
			edgeRows.push({
				supplierFacilityId: previousRow.supplierFacilityId,
				avgResponseTimeMinutes: [previousX, currentX],
				avgResolutionMinutes: [previousY, currentY],
			});
		});
		if (edgeRows.length === 0) return undefined;
		const baseStyle = buildComparisonConnectorEdgeStyle();
		return {
			data: edgeRows,
			style: [
				'supplierFacilityId',
				(supplierFacilityId: any) =>
					hoveredSupplierId !== null &&
					hoveredSupplierId !== undefined &&
					supplierFacilityId === hoveredSupplierId
						? { ...baseStyle, lineWidth: HOVER_CONNECTOR_LINE_WIDTH }
						: baseStyle,
			],
		};
	}, [hasComparison, currentSeriesData, previousSeriesData, hoveredSupplierId]);

	const formatPerformerShape = useCallback((performerGroup, isInternalTeam) => {
		if (isInternalTeam) return PERFORMER_SHAPE.STAR;
		if (performerGroup === PERFORMER_GROUP_LABELS[PERFORMER_SEGMENT.TOP])
			return PERFORMER_SHAPE.STAR;
		if (performerGroup === PERFORMER_GROUP_LABELS[PERFORMER_SEGMENT.BOTTOM])
			return PERFORMER_SHAPE.CROSS;
		return PERFORMER_SHAPE.CIRCLE;
	}, []);

	const performerStyleForColor = useCallback((performerGroup, color) => {
		if (performerGroup === PERFORMER_GROUP_LABELS[PERFORMER_SEGMENT.TOP]) {
			return { fill: color, stroke: color };
		}
		if (performerGroup === PERFORMER_GROUP_LABELS[PERFORMER_SEGMENT.BOTTOM]) {
			return { lineWidth: COMPARISON_POINT_BORDER_WIDTH, fill: null, stroke: color };
		}
		return { lineWidth: COMPARISON_POINT_BORDER_WIDTH, fill: 'transparent', stroke: color };
	}, []);

	const pointColor = hasComparison
		? [
				`supplierFacilityId*${COMPARISON_SERIES_FIELD}`,
				(supplierFacilityId: any, series: any) =>
					series === COMPARISON_SERIES_CURRENT
						? resolveCurrentSeriesPointColor(supplierFacilityId)
						: COMPARISON_PREVIOUS_BORDER_COLOR,
		  ]
		: [
				'supplierFacilityId',
				(supplierFacilityId: any) => resolveCurrentSeriesPointColor(supplierFacilityId),
		  ];

	const pointShape = ['performerGroup*isInternalTeam', formatPerformerShape];

	const sizeForSeries = (series: any) =>
		series === COMPARISON_SERIES_CURRENT
			? COMPARISON_CURRENT_POINT_SIZE
			: COMPARISON_PREVIOUS_POINT_SIZE;
	const withMarkerBoosts = (performerGroup: any, supplierFacilityId: any, base: number) => {
		let size =
			performerGroup === PERFORMER_GROUP_LABELS[PERFORMER_SEGMENT.TOP]
				? base * STAR_SIZE_BOOST
				: base;
		if (
			hoveredSupplierId !== null &&
			hoveredSupplierId !== undefined &&
			supplierFacilityId === hoveredSupplierId
		) {
			size *= HOVER_SIZE_BOOST;
		}
		return size;
	};

	const pointSize = hasComparison
		? [
				`supplierFacilityId*performerGroup*${COMPARISON_SERIES_FIELD}`,
				(supplierFacilityId: any, performerGroup: any, series: any) =>
					withMarkerBoosts(performerGroup, supplierFacilityId, sizeForSeries(series)),
		  ]
		: [
				'supplierFacilityId*performerGroup',
				(supplierFacilityId: any, performerGroup: any) =>
					withMarkerBoosts(performerGroup, supplierFacilityId, COMPARISON_CURRENT_POINT_SIZE),
		  ];

	const pointStyle = hasComparison
		? [
				`supplierFacilityId*performerGroup*${COMPARISON_SERIES_FIELD}`,
				(supplierFacilityId: any, performerGroup: any, series: any) =>
					performerStyleForColor(
						performerGroup,
						series === COMPARISON_SERIES_CURRENT
							? resolveCurrentSeriesPointColor(supplierFacilityId)
							: COMPARISON_PREVIOUS_BORDER_COLOR
					),
		  ]
		: [
				'supplierFacilityId*performerGroup',
				(supplierFacilityId: any, performerGroup: any) =>
					performerStyleForColor(
						performerGroup,
						resolveCurrentSeriesPointColor(supplierFacilityId)
					),
		  ];

	const pointLabel = useMemo(() => {
		return [
			'supplierFacilityName',
			{
				offsetY: -10,
				content: (rowData: any) => {
					if (!showSupplierLabels) return '';
					if (rowData[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_PREVIOUS) return '';
					const name = rowData.supplierFacilityName || '';
					return name.length > MAX_SUPPLIER_LABEL_CHARS
						? name.slice(0, MAX_SUPPLIER_LABEL_CHARS) + '..'
						: name;
				},
				style: { fontSize: 11, fill: 'rgba(0,0,0,0.65)' },
			},
		];
	}, [showSupplierLabels]);

	const buildPerformanceTooltip = useCallback(
		(name, woCount, responseSeconds, resolutionSeconds) => ({
			name: name,
			value: buildGroupedComparisonTooltipHtml(
				[
					{
						swatchColor: chartColor,
						heading: selectedRange
							? formatComparisonRangeLabel(selectedRange[0], selectedRange[1])
							: '',
						rows: buildTooltipMetricRows(responseSeconds, resolutionSeconds),
					},
				],
				{ minWidth: 220, title: name }
			),
		}),
		[chartColor, selectedRange]
	);

	const currentRowsById = useMemo(() => buildRowsById(currentSeriesData), [currentSeriesData]);
	const previousRowsById = useMemo(() => buildRowsById(previousSeriesData), [previousSeriesData]);

	const buildComparisonTooltip = useCallback(
		(supplierFacilityId, supplierFacilityName) => {
			const groups: Array<{
				swatchColor: string;
				heading: string;
				rows: Array<{ label: string; value: string }>;
			}> = [];
			const currentRow = currentRowsById.get(supplierFacilityId);
			if (currentRow) {
				groups.push({
					swatchColor: chartColor,
					heading: selectedRange
						? formatComparisonRangeLabel(selectedRange[0], selectedRange[1])
						: 'Current period',
					rows: buildTooltipMetricRows(
						currentRow.avgResponseTimeSeconds,
						currentRow.avgResolutionTimeSeconds
					),
				});
			}
			const previousRow = previousRowsById.get(supplierFacilityId);
			if (previousRow) {
				groups.push({
					swatchColor: COMPARISON_PREVIOUS_BORDER_COLOR,
					heading: comparisonRange
						? formatComparisonRangeLabel(comparisonRange[0], comparisonRange[1])
						: 'Previous period',
					rows: buildTooltipMetricRows(
						previousRow.avgResponseTimeSeconds,
						previousRow.avgResolutionTimeSeconds
					),
				});
			}
			return {
				name: supplierFacilityName,
				value: buildGroupedComparisonTooltipHtml(groups, {
					minWidth: 220,
					title: supplierFacilityName,
				}),
			};
		},
		[currentRowsById, previousRowsById, selectedRange, comparisonRange, chartColor]
	);

	const pointTooltipDefinition: [string, (...args: any[]) => { name: string; value: string }] =
		hasComparison
			? ['supplierFacilityId*supplierFacilityName', buildComparisonTooltip]
			: [
					'supplierFacilityName*woCount*avgResponseTimeSeconds*avgResolutionTimeSeconds',
					buildPerformanceTooltip,
			  ];

	const formatDurationAxisLabel = useCallback((val) => {
		const minutes = parseFloat(String(val));
		if (isNaN(minutes)) return `${val}`;
		return formatDurationShort(minutes * 60);
	}, []);

	const handlePointClick = useCallback(
		(ev) => {
			const pointData = nullSafeGet('data.data', ev);
			navigateToSupplierDetail(pointData && pointData.supplierFacilityId);
		},
		[navigateToSupplierDetail]
	);

	const renderSupplierName = useCallback(
		(text, record) => (
			<div className="flex items-center gap-2">
				<span className="min-w-0 truncate">{text}</span>
				{record.supplierFacilityId ? (
					<ExportOutlined
						className="shrink-0 cursor-pointer text-character-secondary hover:text-character-primary"
						onClick={(event) => {
							event.stopPropagation();
							navigateToSupplierDetail(record.supplierFacilityId);
						}}
					/>
				) : null}
			</div>
		),
		[navigateToSupplierDetail]
	);
	const renderWoCount = useCallback((value) => value || 0, []);
	const renderDurationColumn = useCallback((value) => formatDurationCell(value), []);
	const renderFirstTimeFixRate = useCallback((value) => formatFirstTimeFixRate(value), []);
	const sortWoCount = useCallback((a, b) => (a.woCount || 0) - (b.woCount || 0), []);
	const sortAvgResponseTime = useCallback(
		(a, b) => (a.avgResponseTimeSeconds || 0) - (b.avgResponseTimeSeconds || 0),
		[]
	);
	const sortAvgResolutionTime = useCallback(
		(a, b) => (a.avgResolutionTimeSeconds || 0) - (b.avgResolutionTimeSeconds || 0),
		[]
	);
	const sortFirstTimeFixRate = useCallback(
		(a, b) => (parseFloat(a.firstTimeFixRate) || 0) - (parseFloat(b.firstTimeFixRate) || 0),
		[]
	);

	const supplierTableColumns = useMemo(
		() =>
			buildSupplierPerformanceTableColumns({
				supplierCount: panelRows.length,
				renderSupplierName,
				renderWoCount,
				renderDurationColumn,
				renderFirstTimeFixRate,
				sortWoCount,
				sortAvgResponseTime,
				sortAvgResolutionTime,
				sortFirstTimeFixRate,
			}),
		[
			panelRows.length,
			renderSupplierName,
			renderWoCount,
			renderDurationColumn,
			renderFirstTimeFixRate,
			sortWoCount,
			sortAvgResponseTime,
			sortAvgResolutionTime,
			sortFirstTimeFixRate,
		]
	);

	const exportSupplierPerformanceCsv = useCallback(() => {
		const currentById = buildRowsById(currentChartRows);
		const dataToExport = panelRows;
		if (dataToExport.length === 0) return;
		const includeHistorical = hasComparison;
		const baseHeaders = [
			'Supplier',
			'WOs',
			'Avg Response Time (seconds)',
			'Avg Response Time',
			'Avg Resolution Time (seconds)',
			'Avg Resolution Time',
			'First Time Fix Rate (%)',
			'Performer Group',
		];
		const headers = includeHistorical ? [...baseHeaders, 'Historical Data'] : baseHeaders;
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'Supplier_Performance_Scorecard',
			headers,
		});
		const buildRow = (row, isHistorical) => {
			const chartRow = isHistorical ? null : currentById.get(row.supplierFacilityId);
			const base = {
				supplier: row.supplierFacilityName,
				woCount: row.woCount,
				avgResponseTimeSeconds:
					row.avgResponseTimeSeconds === null ? '' : row.avgResponseTimeSeconds,
				avgResponseTimeFormatted: formatDurationCell(row.avgResponseTimeSeconds),
				avgResolutionTimeSeconds:
					row.avgResolutionTimeSeconds === null ? '' : row.avgResolutionTimeSeconds,
				avgResolutionTimeFormatted: formatDurationCell(row.avgResolutionTimeSeconds),
				firstTimeFixRate:
					row.firstTimeFixRate === null ? '' : roundToOneDecimalPlace(row.firstTimeFixRate),
				performerGroup: chartRow ? chartRow.performerGroup : '',
			};
			return includeHistorical ? { ...base, historicalData: isHistorical ? 'Yes' : 'No' } : base;
		};
		const currentRows = dataToExport.map((row) => buildRow(row, false));
		const previousRows = includeHistorical
			? previousChartRows.map((row) => buildRow(row, true))
			: [];
		csvExporter.generateCsv([...previousRows, ...currentRows]);
	}, [panelRows, currentChartRows, previousChartRows, hasComparison]);

	const defaultFilters = useMemo(() => getDefaultSupplierPerformanceFilters(), []);
	const isDefaultDateRange =
		activeFilters.startDate &&
		activeFilters.endDate &&
		activeFilters.startDate.format('YYYY-MM-DD') ===
			defaultFilters.startDate.format('YYYY-MM-DD') &&
		activeFilters.endDate.format('YYYY-MM-DD') === defaultFilters.endDate.format('YYYY-MM-DD');
	const activeFilterCount =
		[
			activeFilters.locationIds,
			activeFilters.supplierFacilityIds,
			activeFilters.woPriorityIds,
			activeFilters.problemTypeIds,
		].filter(Boolean).length +
		(activeFilters.isPM !== undefined && activeFilters.isPM !== defaultFilters.isPM ? 1 : 0) +
		(activeFilters.startDate && activeFilters.endDate && !isDefaultDateRange ? 1 : 0) +
		(activeFilters.workCreatedAtStartDate && activeFilters.workCreatedAtEndDate ? 1 : 0);
	const hasActiveFilters = activeFilterCount > 0 || minWoCount !== DEFAULT_MIN_WO_COUNT;
	const hasNoSuppliersInNetwork =
		!loading && !error && normalizedRows.length === 0 && !hasActiveFilters;
	const hasNoFilteredSuppliers = !loading && !error && panelRows.length === 0 && hasActiveFilters;

	const comparisonDisabledReason = !hasFullCompletedAtRange
		? 'Set a Completed between date range filter to enable comparison.'
		: undefined;

	return {
		spinning: loading,
		error,
		hasNoSuppliersInNetwork,
		hasNoFilteredSuppliers,
		navigateToFindVendors,
		clearAllFilters,
		toolbar: {
			chartColor,
			minWoCount,
			setMinWoCount,
			showSupplierLabels,
			setShowSupplierLabels,
			suppliersPanelOpen,
			setSuppliersPanelOpen,
			clearAllFilters,
			hasActiveFilters,
			exportSupplierPerformanceCsv,
			comparison: {
				value: comparisonState,
				onChange: handleComparisonChange,
				selectedStart: selectedRange ? selectedRange[0] : null,
				selectedEnd: selectedRange ? selectedRange[1] : null,
				isTimeBased: true,
				loading: comparisonLoading,
				disabled: !hasFullCompletedAtRange,
				disabledReason: comparisonDisabledReason,
			},
		},
		userType,
		filterValues: activeFilters,
		updateFilters,
		activeFilterCount,
		scatter: {
			chartColor,
			chartData,
			totalChartRowCount: currentChartRows.length,
			hasComparison,
			comparisonMode,
			edges,
			pointColor,
			pointShape,
			pointSize,
			pointStyle,
			pointLabel,
			pointTooltipDefinition,
			formatDurationAxisLabel,
			handlePointClick,
			selectedRange,
			comparisonRange,
			scale: {
				avgResponseTimeMinutes: { nice: true, sync: true },
				avgResolutionMinutes: { nice: true, sync: true },
				performerGroup: { values: PERFORMER_GROUP_LABEL_ORDER },
			},
		},
		panel: {
			panelRows,
			supplierTableColumns,
			onRowClick: (record) => navigateToSupplierDetail(record && record.supplierFacilityId),
		},
	};
}
